import { useEffect, useId, useRef, useState } from 'react';

import { Button, Card, OptionGroup, OptionRow } from '@/design-system';

import { reportCopy } from '../copy';

/**
 * - `keep` — een concept met precies de inhoud van de versie die vaststaat
 *   (`POST /reports/{report}/revise`).
 * - `rebuild` — een concept dat opnieuw uit de opname wordt opgebouwd
 *   (`POST /orders/{order}/report`).
 */
export type NewVersionStart = 'keep' | 'rebuild';

export interface NewVersionConfirmProps {
    /** De versie die nu vaststaat en die de klant houdt tot de nieuwe vaststaat. */
    version: number;
    busy: boolean;
    /**
     * Staat de opname weer open, dan kan opnieuw opbouwen nog niet: het concept
     * volgt dan vanzelf op het indienen. Het id wijst naar de melding daarover.
     */
    rebuildBlockedBy?: string;
    onConfirm: (start: NewVersionStart) => void;
    onCancel: () => void;
}

/**
 * De bevestiging achter "Nieuwe versie": één plek voor wat eerder twee knoppen
 * waren. Je kiest hoe de nieuwe versie begint, en bij elke keuze staat in één zin
 * wat dat betekent - dat verschil is precies wat een beginnend bedrijf niet weet.
 *
 * Standaard staat "Eigen teksten houden" aan: de gewone reden voor een nieuwe
 * versie is een tikfout of een zin die beter kan, en dan wil je niets kwijt.
 */
export function NewVersionConfirm({
    version,
    busy,
    rebuildBlockedBy,
    onConfirm,
    onCancel,
}: NewVersionConfirmProps) {
    const titleId = useId();
    const choiceId = useId();
    const keepHintId = useId();
    const rebuildHintId = useId();
    const confirmRef = useRef<HTMLButtonElement>(null);
    const [start, setStart] = useState<NewVersionStart>('keep');
    const rebuildBlocked = rebuildBlockedBy !== undefined;

    useEffect(() => {
        confirmRef.current?.focus();
    }, []);

    return (
        <Card
            variant="warn"
            padding="lg"
            role="group"
            aria-labelledby={titleId}
            className="mb-[14px] max-w-[780px]"
        >
            <h2
                id={titleId}
                className="m-0 mb-2 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--warn-ink)]"
            >
                {reportCopy.newVersionTitle}
            </h2>
            <p className="m-0 text-[14px] leading-[1.5] text-[var(--warn-ink)]">
                {reportCopy.newVersionBody(version)}
            </p>

            <p
                id={choiceId}
                className="m-0 mt-4 text-[13px] leading-[1.5] font-medium text-[var(--warn-ink)]"
            >
                {reportCopy.newVersionChoice}
            </p>
            <OptionGroup
                type="radio"
                labelledBy={choiceId}
                hasSelection
                className="mt-2 max-w-[620px] px-0 py-0"
            >
                <OptionRow
                    index={0}
                    checked={start === 'keep'}
                    disabled={busy}
                    aria-label={reportCopy.newVersionKeep}
                    aria-describedby={keepHintId}
                    onSelect={() => setStart('keep')}
                >
                    <span className="block">{reportCopy.newVersionKeep}</span>
                    <span
                        id={keepHintId}
                        className="mt-1 block text-[13px] font-normal text-[var(--text-secondary)]"
                    >
                        {reportCopy.newVersionKeepHint(version)}
                    </span>
                </OptionRow>
                <OptionRow
                    index={1}
                    checked={start === 'rebuild'}
                    disabled={busy || rebuildBlocked}
                    aria-label={reportCopy.newVersionRebuild}
                    aria-describedby={
                        rebuildBlocked ? `${rebuildHintId} ${rebuildBlockedBy}` : rebuildHintId
                    }
                    onSelect={() => setStart('rebuild')}
                >
                    <span className="block">{reportCopy.newVersionRebuild}</span>
                    <span
                        id={rebuildHintId}
                        className="mt-1 block text-[13px] font-normal text-[var(--text-secondary)]"
                    >
                        {rebuildBlocked
                            ? `${reportCopy.newVersionRebuildHint(version)} ${reportCopy.newVersionRebuildSurveyOpen}`
                            : reportCopy.newVersionRebuildHint(version)}
                    </span>
                </OptionRow>
            </OptionGroup>

            <div className="mt-4 flex flex-wrap gap-2">
                <Button
                    ref={confirmRef}
                    variant="primary"
                    size="md"
                    disabled={busy}
                    onClick={() => onConfirm(start)}
                >
                    {reportCopy.newVersionConfirm}
                </Button>
                <Button variant="outline" size="md" disabled={busy} onClick={onCancel}>
                    {reportCopy.newVersionCancel}
                </Button>
            </div>
        </Card>
    );
}
